import React, { useState } from 'react';

import { Alert, Box, Button, FormControlLabel, Paper, Stack, Switch, Typography } from '@mui/material';

import { useUpdatePackage } from '~/hooks/apis/offers/useUpdatePackage';

import getErrorMessage from '~/utils/getErrorMessage';

import { getOfferPackageName, getPackagePersistId } from './utils';

type Props = {
  disabled?: boolean;
  formId: string;
  offerId: string;
  onSaved: () => void | Promise<void>;
  onSavingChange?: (isSaving: boolean) => void;
  packages: Array<App.Package>;
};

function getMaxExtraNights(pkg: App.Package): number {
  return 'max_extra_nights' in pkg && typeof pkg.max_extra_nights === 'number' ? pkg.max_extra_nights : 0;
}

function buildInitialFlexibleNights(packages: Array<App.Package>): Record<string, boolean> {
  return Object.fromEntries(
    packages.flatMap((pkg) => {
      const packageId = getPackagePersistId(pkg);
      return packageId ? [[packageId, 'flexible_nights' in pkg && !!pkg.flexible_nights]] : [];
    }),
  );
}

function PackageSetupStep({ disabled = false, formId, offerId, onSaved, onSavingChange, packages }: Props) {
  const updatePackageMutation = useUpdatePackage();
  const [flexibleNightsById, setFlexibleNightsById] = useState<Record<string, boolean>>(() =>
    buildInitialFlexibleNights(packages),
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [confirmedCount, setConfirmedCount] = useState(0);

  const flexibleNightsWithoutMaxNights = packages.flatMap((pkg) => {
    const packageId = getPackagePersistId(pkg);
    const isFlexibleNights = (packageId && flexibleNightsById[packageId]) ?? false;
    const hasNoMaxNights = getMaxExtraNights(pkg) === 0;
    return packageId && isFlexibleNights && hasNoMaxNights ? [{ packageId, name: getOfferPackageName(pkg) }] : [];
  });
  const nextStepsCount = flexibleNightsWithoutMaxNights.length || confirmedCount;

  async function handleConfirmDisableFlexibleNights() {
    const targets = flexibleNightsWithoutMaxNights;

    if (targets.length === 0) {
      return;
    }

    setErrorMessage(null);
    onSavingChange?.(true);

    const results = await Promise.allSettled(
      targets.map(({ packageId }) =>
        updatePackageMutation.mutateAsync({
          offerId,
          packageId,
          packageData: { flexible_nights: false },
        }),
      ),
    );

    onSavingChange?.(false);

    const failureIndex = results.findIndex((result) => result.status === 'rejected');

    if (failureIndex !== -1) {
      const failure = results[failureIndex] as PromiseRejectedResult;
      setErrorMessage(`Failed to confirm "${targets[failureIndex].name}": ${getErrorMessage(failure.reason)}`);
      return;
    }

    setFlexibleNightsById((prev) => {
      const next = { ...prev };
      targets.forEach(({ packageId }) => {
        next[packageId] = false;
      });
      return next;
    });
    setConfirmedCount(targets.length);
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setErrorMessage(null);

    if (flexibleNightsWithoutMaxNights.length > 0) {
      return;
    }

    onSavingChange?.(true);

    const editablePackages = packages.flatMap((pkg) => {
      const packageId = getPackagePersistId(pkg);
      const flexibleNights = packageId ? flexibleNightsById[packageId] : undefined;
      return packageId !== undefined && flexibleNights !== undefined ? [{ flexibleNights, pkg, packageId }] : [];
    });

    const results = await Promise.allSettled(
      editablePackages.map(({ flexibleNights, packageId }) =>
        updatePackageMutation.mutateAsync({
          offerId,
          packageId,
          packageData: { flexible_nights: flexibleNights },
        }),
      ),
    );

    const failureIndex = results.findIndex((result) => result.status === 'rejected');

    onSavingChange?.(false);

    if (failureIndex !== -1) {
      const failure = results[failureIndex] as PromiseRejectedResult;
      const failedPackageName = getOfferPackageName(editablePackages[failureIndex].pkg);
      setErrorMessage(`Failed to save "${failedPackageName}": ${getErrorMessage(failure.reason)}`);
      return;
    }

    await onSaved();
  }

  return (
    <Stack spacing={2}>
      {flexibleNightsWithoutMaxNights.length > 0 && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" disabled={disabled} size="small" onClick={handleConfirmDisableFlexibleNights}>
              Confirm
            </Button>
          }
        >
          <Typography variant="body2">
            We were unable to link{' '}
            {flexibleNightsWithoutMaxNights.length === 1 ? 'this Deal Option' : 'these Deal Options'}, since{' '}
            {flexibleNightsWithoutMaxNights.length === 1 ? 'it is' : 'they are'} set to Flexi Night but there is no Max
            Nights set. In order to continue, please click &quot;Confirm&quot;, which will set Flexi Nights to FALSE in
            the package on Admin Portal, but will not change Salesforce:
          </Typography>
          <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
            {flexibleNightsWithoutMaxNights.map((pkg) => (
              <li key={pkg.packageId}>{pkg.name}</li>
            ))}
          </Box>
        </Alert>
      )}

      {nextStepsCount > 0 && (
        <>
          <Typography variant="body2" color="text.secondary">
            Once you have finished setting up the offer, complete these steps:
          </Typography>
          <Box component="ol" sx={{ pl: 2, mt: -1 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              Set max nights for {nextStepsCount === 1 ? 'this deal option' : 'these deal options'} in Salesforce.
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              {nextStepsCount === 1 ? 'This Deal Option' : 'These Deal Options'} will be automatically synced into Admin
              Portal once this is done.
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Set Flexi Nights to TRUE for {nextStepsCount === 1 ? 'this package' : 'these packages'} in Admin Portal.
              This will be flagged in the CM checklist.
            </Typography>
          </Box>
        </>
      )}

      <form id={formId} onSubmit={handleSubmit}>
        <Stack spacing={2}>
          {packages.map((pkg) => {
            const packageId = getPackagePersistId(pkg);
            const isFlexibleNights = (packageId && flexibleNightsById[packageId]) ?? false;
            const maxExtraNights = getMaxExtraNights(pkg);
            // Enabling requires a real synced max-nights value, so AP can't drift from Salesforce.
            const canEnableFlexibleNights = maxExtraNights > 0;

            return (
              <Paper key={packageId} variant="outlined" sx={{ borderRadius: 1.25, p: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography sx={{ flex: 1 }}>{getOfferPackageName(pkg)}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
                    Max extra nights (Salesforce): {maxExtraNights > 0 ? maxExtraNights : 'none set'}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isFlexibleNights}
                        disabled={disabled || (!isFlexibleNights && !canEnableFlexibleNights)}
                        onChange={(event) =>
                          packageId &&
                          setFlexibleNightsById((prev) => ({
                            ...prev,
                            [packageId]: event.target.checked,
                          }))
                        }
                      />
                    }
                    label="Flexible nights"
                  />
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      </form>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
    </Stack>
  );
}

export default PackageSetupStep;
